import { useCallback, useEffect, useState } from 'react';
import { apiDelete, apiGetResponse, apiPatch, apiPost, getAccessToken } from '../lib/api-client';

export interface NotificationItem { id: number; type: string; title: string; message: string; link: string | null; readAt: string | null; createdAt: string | null; }
interface NotificationResponse { rows: NotificationItem[]; pagination: { page: number; limit: number; total: number; pages: number }; unreadCount: number; }

export function useNotifications(limit = 20) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState<NotificationResponse['pagination'] | null>(null);
  const load = useCallback(async (page = 1) => {
    const response = await apiGetResponse<NotificationResponse>('/notifications', { page, limit });
    setItems(response.rows); setUnreadCount(response.unreadCount); setPagination(response.pagination);
  }, [limit]);

  useEffect(() => {
    const initialLoad = window.setTimeout(() => void load(), 0);
    const token = getAccessToken();
    if (!token) return () => window.clearTimeout(initialLoad);
    const base = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;
    const socketUrl = `${base.replace(/^http/, 'ws')}/ws/notifications?token=${encodeURIComponent(token)}`;
    let socket: WebSocket | undefined;
    try {
      socket = new WebSocket(socketUrl);
      socket.onmessage = () => { void load(); };
    } catch { /* REST remains the fallback when WebSocket is unavailable. */ }
    const interval = window.setInterval(() => void load(), 30000);
    return () => { window.clearTimeout(initialLoad); socket?.close(); window.clearInterval(interval); };
  }, [load]);

  const markRead = async (id: number) => { await apiPatch(`/notifications/${id}/read`); await load(pagination?.page ?? 1); };
  const remove = async (id: number) => { await apiDelete(`/notifications/${id}`); await load(pagination?.page ?? 1); };
  const markAllRead = async () => { await apiPost('/notifications/read-all'); await load(pagination?.page ?? 1); };
  return { items, unreadCount, pagination, load, markRead, remove, markAllRead };
}
